import { PERMISSIONS, WILDCARD_PERMISSION, type PermissionGrant } from './permissions';

/**
 * Role *keys* are stable identifiers used by seeds and by code that needs to reason about a role.
 * Role rows themselves are per-tenant, so a tenant may rename or extend them without breaking code.
 */
export const RoleKey = {
  PLATFORM_SUPER_ADMIN: 'PLATFORM_SUPER_ADMIN',
  PLATFORM_SUPPORT: 'PLATFORM_SUPPORT',
  TENANT_OWNER: 'TENANT_OWNER',
  BRANCH_MANAGER: 'BRANCH_MANAGER',
  SALES_MANAGER: 'SALES_MANAGER',
  SALES_EMPLOYEE: 'SALES_EMPLOYEE',
  STOCK_MANAGER: 'STOCK_MANAGER',
  OPTOMETRIST: 'OPTOMETRIST',
  ACCOUNTANT: 'ACCOUNTANT',
} as const;
export type RoleKey = (typeof RoleKey)[keyof typeof RoleKey];

export interface RoleTemplate {
  key: RoleKey;
  name: string;
  nameAr: string;
  description: string;
  /** Platform roles live outside any tenant; tenant roles are cloned into each new tenant. */
  isPlatformRole: boolean;
  /** A user holding this role implicitly reaches every branch of the tenant. */
  grantsAllBranches: boolean;
  permissions: readonly PermissionGrant[];
}

/**
 * The roles every tenant starts with.
 *
 * Two of them exist to keep money and stock apart. A Sales Manager may refund a customer but
 * cannot declare a returned frame sellable; a Stock Manager decides the fate of returned goods but
 * cannot take money or reverse a sale. That separation is a control, not a convenience: whoever
 * hands back the cash should not also be the one who says the goods came back in good condition.
 *
 * Authorization is never by role name. These templates only decide which permissions a role starts
 * with, and a tenant is free to rewrite them; every guard in the system matches permissions.
 */
export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  {
    key: RoleKey.PLATFORM_SUPER_ADMIN,
    name: 'Platform Super Admin',
    nameAr: 'مدير المنصة',
    description: 'Full control over the SaaS platform, tenants and subscriptions.',
    isPlatformRole: true,
    grantsAllBranches: true,
    permissions: [WILDCARD_PERMISSION],
  },
  {
    key: RoleKey.PLATFORM_SUPPORT,
    name: 'Platform Support',
    nameAr: 'دعم المنصة',
    description: 'Read-only visibility across tenants for support staff.',
    isPlatformRole: true,
    grantsAllBranches: true,
    permissions: [
      PERMISSIONS.PLATFORM_TENANTS_VIEW,
      PERMISSIONS.PLATFORM_USERS_VIEW,
      PERMISSIONS.PLATFORM_AUDIT_VIEW,
    ],
  },
  {
    key: RoleKey.TENANT_OWNER,
    name: 'Owner',
    nameAr: 'المالك',
    description: 'Owns the optical company. Full access to every branch and every module.',
    isPlatformRole: false,
    grantsAllBranches: true,
    permissions: [
      PERMISSIONS.TENANT_VIEW,
      PERMISSIONS.TENANT_UPDATE,
      'branches.*',
      'users.*',
      'roles.*',
      PERMISSIONS.AUDIT_VIEW,
      'customers.*',
      'catalog.*',
      'inventory.*',
      'sales.*',
      'payments.*',
      // Everything clinical except signing an examination off, which is a clinical act: an owner who examines is also given the Optometrist role.
      PERMISSIONS.MEDICAL_VIEW,
      PERMISSIONS.MEDICAL_CREATE,
      PERMISSIONS.MEDICAL_UPDATE,
      PERMISSIONS.MEDICAL_VOID,
      'measurements.*',
      'face.*',
      'frame.*',
      'optical.*',
      'suppliers.*',
      'purchasing.*',
      'payables.*',
      'expenses.*',
      'attendance.*',
      'notifications.*',
      'automations.*',
      'wholesale.*',
      'reporting.*',
    ],
  },
  {
    key: RoleKey.BRANCH_MANAGER,
    name: 'Branch Manager',
    nameAr: 'مدير الفرع',
    description: 'Runs one or more branches: staff, stock, sales and expense approval.',
    isPlatformRole: false,
    grantsAllBranches: false,
    permissions: [
      PERMISSIONS.TENANT_VIEW,
      PERMISSIONS.BRANCHES_VIEW,
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.ROLES_VIEW,
      'customers.*',
      PERMISSIONS.CATALOG_VIEW,
      'inventory.*',
      'sales.*',
      'payments.*',
      PERMISSIONS.MEDICAL_VIEW,
      PERMISSIONS.MEASUREMENTS_VIEW,
      PERMISSIONS.FACE_ASSESS,
      PERMISSIONS.FRAME_RECOMMEND,
      // Runs the branch's jobs end to end and may cancel one, since it may also refund. The tenant's lab list is the owner's, like its suppliers.
      PERMISSIONS.OPTICAL_VIEW,
      PERMISSIONS.OPTICAL_CREATE,
      PERMISSIONS.OPTICAL_UPDATE,
      PERMISSIONS.OPTICAL_LAB_WORKFLOW,
      PERMISSIONS.OPTICAL_DELIVER,
      PERMISSIONS.OPTICAL_CANCEL,
      // Buys for the branch end to end: drafts, places, receives, cancels and records the supplier's bill. The supplier list itself stays the owner's.
      PERMISSIONS.SUPPLIERS_VIEW,
      PERMISSIONS.PURCHASING_VIEW,
      PERMISSIONS.PURCHASING_CREATE,
      PERMISSIONS.PURCHASING_UPDATE,
      PERMISSIONS.PURCHASING_APPROVE,
      PERMISSIONS.PURCHASING_RECEIVE,
      PERMISSIONS.PURCHASING_CANCEL,
      PERMISSIONS.PURCHASING_INVOICE,
      // Settles what the branch has been invoiced for, within the branches it runs.
      PERMISSIONS.PAYABLES_VIEW,
      PERMISSIONS.PAYABLES_PAY,
      PERMISSIONS.EXPENSES_VIEW,
      // Records what the branch spends running itself. The category master is tenant-wide, so
      // maintaining it is a deliberate grant rather than part of running one branch.
      PERMISSIONS.EXPENSES_MANAGE,
      PERMISSIONS.EXPENSES_CREATE,
      PERMISSIONS.EXPENSES_APPROVE,
      PERMISSIONS.ATTENDANCE_VIEW,
      PERMISSIONS.ATTENDANCE_MANAGE,
      // Messages customers and reads what was said to them. The template master is tenant-wide wording, so it stays with the owner; changing which number a branch messages from is `branches.update`, which a branch manager does not hold.
      PERMISSIONS.NOTIFICATIONS_VIEW,
      PERMISSIONS.NOTIFICATIONS_SEND,
      PERMISSIONS.REPORTING_VIEW,
    ],
  },
  {
    key: RoleKey.SALES_MANAGER,
    name: 'Sales Manager',
    nameAr: 'مدير المبيعات',
    description:
      'Runs the counter: discounts, returns and refunds, without authority over stock balances.',
    isPlatformRole: false,
    grantsAllBranches: false,
    permissions: [
      PERMISSIONS.BRANCHES_VIEW,
      'customers.*',
      PERMISSIONS.CATALOG_VIEW,
      // Sees stock in order to sell it; correcting a balance is somebody else's job.
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.SALES_VIEW,
      PERMISSIONS.SALES_CREATE,
      PERMISSIONS.SALES_REFUND,
      PERMISSIONS.SALES_DISCOUNT,
      'payments.*',
      PERMISSIONS.MEASUREMENTS_VIEW,
      PERMISSIONS.FACE_ASSESS,
      PERMISSIONS.FRAME_RECOMMEND,
      // The counter's jobs, including cancelling one before the lab, which goes with the refund authority this role already holds.
      PERMISSIONS.OPTICAL_VIEW,
      PERMISSIONS.OPTICAL_CREATE,
      PERMISSIONS.OPTICAL_UPDATE,
      PERMISSIONS.OPTICAL_LAB_WORKFLOW,
      PERMISSIONS.OPTICAL_DELIVER,
      PERMISSIONS.OPTICAL_CANCEL,
      PERMISSIONS.NOTIFICATIONS_VIEW,
      PERMISSIONS.NOTIFICATIONS_SEND,
      PERMISSIONS.REPORTING_VIEW,
    ],
  },
  {
    key: RoleKey.SALES_EMPLOYEE,
    name: 'Sales Employee',
    nameAr: 'موظف مبيعات',
    description: 'Front-of-house staff working a single branch.',
    isPlatformRole: false,
    grantsAllBranches: false,
    permissions: [
      PERMISSIONS.BRANCHES_VIEW,
      PERMISSIONS.CUSTOMERS_VIEW,
      PERMISSIONS.CUSTOMERS_CREATE,
      PERMISSIONS.CUSTOMERS_EDIT,
      PERMISSIONS.CATALOG_VIEW,
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.SALES_VIEW,
      PERMISSIONS.SALES_CREATE,
      PERMISSIONS.PAYMENTS_VIEW,
      PERMISSIONS.PAYMENTS_CREATE,
      PERMISSIONS.MEASUREMENTS_VIEW,
      PERMISSIONS.MEASUREMENTS_CREATE,
      PERMISSIONS.FACE_ASSESS,
      PERMISSIONS.FRAME_RECOMMEND,
      // Takes, tracks and hands over jobs. Cancelling one returns money, which this role cannot do.
      PERMISSIONS.OPTICAL_VIEW,
      PERMISSIONS.OPTICAL_CREATE,
      PERMISSIONS.OPTICAL_UPDATE,
      PERMISSIONS.OPTICAL_LAB_WORKFLOW,
      PERMISSIONS.OPTICAL_DELIVER,
      PERMISSIONS.NOTIFICATIONS_VIEW,
      PERMISSIONS.NOTIFICATIONS_SEND,
    ],
  },
  {
    key: RoleKey.STOCK_MANAGER,
    name: 'Stock Manager',
    nameAr: 'مدير المخزون',
    description:
      'Owns stock: receiving, counts, adjustments, transfers and the fate of returned goods — but takes no money.',
    isPlatformRole: false,
    grantsAllBranches: false,
    permissions: [
      PERMISSIONS.BRANCHES_VIEW,
      PERMISSIONS.CATALOG_VIEW,
      PERMISSIONS.CATALOG_CREATE,
      PERMISSIONS.CATALOG_EDIT,
      'inventory.*',
      // Drafts and receives purchase orders but never places one: committing the tenant's money stays with a branch manager or the owner.
      PERMISSIONS.SUPPLIERS_VIEW,
      PERMISSIONS.PURCHASING_VIEW,
      PERMISSIONS.PURCHASING_CREATE,
      PERMISSIONS.PURCHASING_UPDATE,
      PERMISSIONS.PURCHASING_RECEIVE,
      // Reads orders to understand why stock moved; cannot ring one up or reverse one.
      PERMISSIONS.SALES_VIEW,
      PERMISSIONS.REPORTING_VIEW,
    ],
  },
  {
    key: RoleKey.OPTOMETRIST,
    name: 'Optometrist',
    nameAr: 'أخصائي بصريات',
    description: 'Performs eye examinations and records prescriptions.',
    isPlatformRole: false,
    grantsAllBranches: false,
    permissions: [
      PERMISSIONS.CUSTOMERS_VIEW,
      PERMISSIONS.CUSTOMERS_CREATE,
      'medical.*',
      'measurements.*',
      PERMISSIONS.CATALOG_VIEW,
      // Reads the jobs made up for a customer, to check a finished pair against the prescription; moves none of them.
      PERMISSIONS.OPTICAL_VIEW,
      // Sees what a customer has been told, and tells them nothing: messaging the customer is the counter's job.
      PERMISSIONS.NOTIFICATIONS_VIEW,
    ],
  },
  {
    key: RoleKey.ACCOUNTANT,
    name: 'Accountant',
    nameAr: 'محاسب',
    description: 'Financial oversight across branches without operational access.',
    isPlatformRole: false,
    grantsAllBranches: true,
    permissions: [
      PERMISSIONS.BRANCHES_VIEW,
      PERMISSIONS.SALES_VIEW,
      PERMISSIONS.PAYMENTS_VIEW,
      PERMISSIONS.PURCHASING_VIEW,
      // Sees what is owed across every branch; paying it is an explicit grant a tenant makes deliberately.
      PERMISSIONS.PAYABLES_VIEW,
      PERMISSIONS.EXPENSES_VIEW,
      PERMISSIONS.EXPENSES_MANAGE,
      PERMISSIONS.EXPENSES_APPROVE,
      'reporting.*',
    ],
  },
];

export const TENANT_ROLE_TEMPLATES = ROLE_TEMPLATES.filter((role) => !role.isPlatformRole);
export const PLATFORM_ROLE_TEMPLATES = ROLE_TEMPLATES.filter((role) => role.isPlatformRole);
